import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Users, Phone, Mail, MapPin, StickyNote, Tag, Briefcase,
  DollarSign, Pencil, Trash2, Link2, Copy, ExternalLink,
  Loader2, Upload, Image, FileText, CreditCard, Eye,
  Download, Calendar, ChevronRight, X, Clock, Activity
} from 'lucide-react';
import { ClientActivityTab } from '@/components/orders/OrderTimeline';
import { SecureImage } from '@/components/ui/SecureImage';
import { downloadFile, getSecureImageUrl } from '@/lib/fileUtils';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// ============ OVERVIEW TAB ============
function OverviewTab({ client, onEdit, onCreateOrder, onOpenChange }) {
  const { api } = useAuth();
  const [portalLink, setPortalLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);

  const handleGeneratePortalLink = async () => {
    setGeneratingLink(true);
    try {
      const res = await api.post(`/portal/generate-link?client_id=${client.id}`);
      setPortalLink(`${window.location.origin}/portal?token=${res.data.token}`);
      toast.success('Ссылка создана');
    } catch { toast.error('Ошибка создания ссылки'); }
    finally { setGeneratingLink(false); }
  };

  return (
    <div className="space-y-5">
      {client.pinned_note && (
        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 text-sm font-medium mb-1">
            <StickyNote className="h-4 w-4" /> Важная заметка
          </div>
          <p className="text-sm">{client.pinned_note}</p>
        </div>
      )}
      <div className="space-y-3">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Контакты</h4>
        {client.phone && (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center"><Phone className="h-4 w-4 text-accent" /></div>
            <div><p className="font-medium text-sm">{client.phone}</p><p className="text-xs text-muted-foreground">Основной</p></div>
          </div>
        )}
        {client.email && (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center"><Mail className="h-4 w-4 text-accent" /></div>
            <div><p className="font-medium text-sm">{client.email}</p><p className="text-xs text-muted-foreground">Email</p></div>
          </div>
        )}
        {client.address_string && (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center"><MapPin className="h-4 w-4 text-accent" /></div>
            <div><p className="font-medium text-sm">{client.address_string}</p><p className="text-xs text-muted-foreground">Адрес</p></div>
          </div>
        )}
      </div>
      {client.tags?.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Теги</h4>
          <div className="flex flex-wrap gap-1.5">
            {client.tags.map((tag, i) => (<Badge key={i} variant="secondary" className="text-xs"><Tag className="h-3 w-3 mr-1" />{tag}</Badge>))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-lg bg-muted/50">
          <div className="flex items-center gap-2 text-muted-foreground mb-1"><Briefcase className="h-4 w-4" /><span className="text-xs">Заказов</span></div>
          <p className="text-xl font-bold tabular-nums">{client.orders_count || 0}</p>
        </div>
        <div className="p-3 rounded-lg bg-muted/50">
          <div className="flex items-center gap-2 text-muted-foreground mb-1"><DollarSign className="h-4 w-4" /><span className="text-xs">Баланс</span></div>
          <p className={cn("text-xl font-bold tabular-nums", (client.balance||0)<0 && "text-destructive", (client.balance||0)>0 && "text-green-600")}>{(client.balance||0).toFixed(2)}</p>
        </div>
      </div>
      <div className="space-y-3 pt-3 border-t">
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2"><Link2 className="h-4 w-4" />Клиентский портал</h4>
          {portalLink ? (
            <div className="p-3 rounded-lg bg-muted/50 space-y-2">
              <p className="text-xs text-muted-foreground break-all">{portalLink}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => { navigator.clipboard.writeText(portalLink); toast.success('Скопировано'); }} data-testid="copy-portal-link"><Copy className="mr-1 h-3 w-3" />Копировать</Button>
                <Button size="sm" variant="outline" onClick={() => window.open(portalLink, '_blank')} data-testid="open-portal-link"><ExternalLink className="h-3 w-3" /></Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" className="w-full" onClick={handleGeneratePortalLink} disabled={generatingLink} data-testid="generate-portal-link">
              {generatingLink ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}Создать ссылку на портал
            </Button>
          )}
        </div>
        <Button className="w-full" onClick={() => { onCreateOrder(client); onOpenChange(false); }} data-testid="client-create-order"><Briefcase className="mr-2 h-4 w-4" />Создать заказ</Button>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => { onEdit(client); onOpenChange(false); }} data-testid="client-detail-edit"><Pencil className="mr-2 h-4 w-4" />Редактировать</Button>
        </div>
      </div>
    </div>
  );
}

// ============ ORDERS TAB ============
function OrdersTab({ clientId }) {
  const { api } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    api.get(`/clients/${clientId}/orders?limit=100`).then(res => {
      setOrders(res.data.orders || []);
      setTotal(res.data.total_count || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [clientId, api]);

  const statusColors = { new: 'bg-blue-100 text-blue-800', confirmed: 'bg-indigo-100 text-indigo-800', in_progress: 'bg-yellow-100 text-yellow-800', done: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800' };
  const payColors = { paid: 'bg-green-100 text-green-800', unpaid: 'bg-red-100 text-red-800', partial: 'bg-yellow-100 text-yellow-800', refund: 'bg-gray-100 text-gray-800' };

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!orders.length) return <div className="text-center py-8 text-muted-foreground"><Briefcase className="h-10 w-10 mx-auto mb-2 opacity-30" /><p className="text-sm">Нет заказов</p></div>;

  return (
    <div className="space-y-2" data-testid="client-orders-tab">
      <p className="text-xs text-muted-foreground mb-3">Всего заказов: {total}</p>
      {orders.map(o => (
        <div key={o.id} className="p-3 rounded-lg border hover:bg-muted/30 transition-colors">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm truncate">{o.title || 'Без названия'}</p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {o.scheduled_date && <span className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="h-3 w-3" />{String(o.scheduled_date).slice(0,10)}</span>}
                {o.scheduled_time_start && <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{o.scheduled_time_start}</span>}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", statusColors[o.operational_status] || 'bg-gray-100')}>{o.operational_status}</span>
              <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", payColors[o.payment_status] || 'bg-gray-100')}>{o.payment_status}</span>
            </div>
          </div>
          {o.assigned_workers?.length > 0 && (
            <div className="flex gap-1 mt-2 flex-wrap">
              {o.assigned_workers.map(w => (
                <span key={w.id} className="text-xs px-2 py-0.5 rounded-full border" style={{borderColor: w.color}}>{w.name}</span>
              ))}
            </div>
          )}
          <div className="flex justify-between items-center mt-2">
            {o.service_type_name && <span className="text-xs text-muted-foreground">{o.service_type_name}</span>}
            <span className="text-sm font-medium tabular-nums">${(o.total_amount || 0).toLocaleString()}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ PHOTOS TAB ============
function PhotosTab({ clientId }) {
  const { api } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewImg, setPreviewImg] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    api.get(`/clients/${clientId}/photos`).then(res => setGroups(res.data || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [clientId, api]);

  // SEC-01 FIX: Load preview image securely
  const handlePreview = async (fileId) => {
    setPreviewLoading(true);
    try {
      const url = await getSecureImageUrl(fileId);
      setPreviewImg(url);
    } catch {
      toast.error('Ошибка загрузки изображения');
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!groups.length) return <div className="text-center py-8 text-muted-foreground"><Image className="h-10 w-10 mx-auto mb-2 opacity-30" /><p className="text-sm">Нет фотографий</p></div>;

  const renderSection = (label, photos, color) => {
    if (!photos?.length) return null;
    return (
      <div className="space-y-2">
        <h5 className={cn("text-xs font-semibold uppercase tracking-wide", color)}>{label} ({photos.length})</h5>
        <div className="grid grid-cols-3 gap-2">
          {photos.map(p => (
            <button key={p.id} onClick={() => handlePreview(p.file_id)}
              className="aspect-square rounded-lg overflow-hidden border hover:ring-2 ring-accent transition-all bg-muted" data-testid={`photo-thumb-${p.id}`}>
              <SecureImage fileId={p.file_id} alt={p.title || ''} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="client-photos-tab">
      {groups.map((g, i) => (
        <div key={i} className="space-y-3">
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">{g.order_title || 'Без заказа'}</span>
            {g.order_date && <span className="text-xs text-muted-foreground">{g.order_date}</span>}
          </div>
          {renderSection('До', g.before, 'text-orange-600')}
          {renderSection('После', g.after, 'text-green-600')}
          {renderSection('Прочие', g.other, 'text-blue-600')}
        </div>
      ))}
      {/* Lightbox */}
      <Dialog open={!!previewImg || previewLoading} onOpenChange={() => setPreviewImg(null)}>
        <DialogContent className="max-w-3xl p-2">
          <DialogHeader><DialogTitle className="sr-only">Просмотр фото</DialogTitle></DialogHeader>
          {previewLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : previewImg && <img src={previewImg} alt="Preview" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ DOCUMENTS TAB ============
function DocumentsTab({ clientId }) {
  const { api } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    api.get(`/clients/${clientId}/documents`).then(res => setDocs(res.data || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [clientId, api]);

  useEffect(() => { load(); }, [load]);

  const typeLabels = { contract: 'Договор', signed_contract: 'Подписан', invoice: 'Счёт', receipt: 'Квитанция', estimate: 'Смета', other: 'Другое' };
  const typeColors = { contract: 'bg-blue-100 text-blue-800', signed_contract: 'bg-green-100 text-green-800', invoice: 'bg-purple-100 text-purple-800', receipt: 'bg-teal-100 text-teal-800', estimate: 'bg-orange-100 text-orange-800', other: 'bg-gray-100 text-gray-800' };

  // SEC-01 FIX: Secure download/view without token in URL
  const handleDownload = async (fileId, filename) => {
    try {
      await downloadFile(fileId, filename);
    } catch {
      toast.error('Ошибка скачивания');
    }
  };
  
  const handleView = async (fileId, contentType) => {
    try {
      const response = await api.get(`/files/${fileId}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: contentType || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error('Ошибка открытия файла');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('asset_type', 'other');
    fd.append('title', file.name);
    try {
      await api.post(`/clients/${clientId}/assets/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Документ загружен');
      load();
    } catch { toast.error('Ошибка загрузки'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-3" data-testid="client-documents-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{docs.length} документ(ов)</p>
        <label className="cursor-pointer">
          <input type="file" className="hidden" onChange={handleUpload} accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.html" disabled={uploading} />
          <Button size="sm" variant="outline" asChild disabled={uploading} data-testid="upload-document-btn">
            <span>{uploading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Upload className="mr-1 h-3 w-3" />}Загрузить</span>
          </Button>
        </label>
      </div>
      {!docs.length ? (
        <div className="text-center py-8 text-muted-foreground"><FileText className="h-10 w-10 mx-auto mb-2 opacity-30" /><p className="text-sm">Нет документов</p></div>
      ) : docs.map(d => (
        <div key={d.id} className="p-3 rounded-lg border hover:bg-muted/30 transition-colors">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm truncate">{d.title || d.filename || 'Документ'}</p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", typeColors[d.asset_type] || typeColors.other)}>{typeLabels[d.asset_type] || d.asset_type}</span>
                {d.document_status && <span className="text-xs text-muted-foreground">Статус: {d.document_status}</span>}
                <span className="text-xs text-muted-foreground">{d.created_at ? new Date(d.created_at).toLocaleDateString('ru') : ''}</span>
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleView(d.file_id, d.content_type)} data-testid={`doc-view-${d.id}`} title="Открыть"><Eye className="h-3.5 w-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDownload(d.file_id, d.title || d.filename || 'document')} data-testid={`doc-download-${d.id}`} title="Скачать"><Download className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ PAYMENTS TAB ============
function PaymentsTab({ clientId }) {
  const { api } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    api.get(`/clients/${clientId}/payments`).then(res => setPayments(res.data || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [clientId, api]);

  const statusLabels = { pending: 'Ожидание', processing: 'Обработка', succeeded: 'Оплачено', failed: 'Ошибка', refunded: 'Возврат', cancelled: 'Отменён' };
  const statusColors = { succeeded: 'bg-green-100 text-green-800', pending: 'bg-yellow-100 text-yellow-800', failed: 'bg-red-100 text-red-800', refunded: 'bg-gray-100 text-gray-800' };

  // SEC-01 FIX: Secure file view without token in URL
  const handleViewFile = async (fileId) => {
    try {
      const response = await api.get(`/files/${fileId}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error('Ошибка открытия файла');
    }
  };

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!payments.length) return <div className="text-center py-8 text-muted-foreground"><CreditCard className="h-10 w-10 mx-auto mb-2 opacity-30" /><p className="text-sm">Нет платежей</p></div>;

  return (
    <div className="space-y-2" data-testid="client-payments-tab">
      {payments.map(p => (
        <div key={p.id} className="p-3 rounded-lg border hover:bg-muted/30 transition-colors">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-bold text-base tabular-nums">${(p.amount || 0).toLocaleString()}</p>
              {p.order_title && <p className="text-xs text-muted-foreground mt-0.5 truncate">{p.order_title}</p>}
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", statusColors[p.status] || 'bg-gray-100 text-gray-800')}>{statusLabels[p.status] || p.status}</span>
                <span className="text-xs text-muted-foreground">{p.provider || 'manual'}</span>
                <span className="text-xs text-muted-foreground">{p.paid_at ? new Date(p.paid_at).toLocaleDateString('ru') : p.created_at ? new Date(p.created_at).toLocaleDateString('ru') : ''}</span>
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              {p.proof_file_id && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleViewFile(p.proof_file_id)} data-testid={`payment-proof-${p.id}`}><Eye className="mr-1 h-3 w-3" />Чек</Button>
              )}
              {p.invoice_file_id && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleViewFile(p.invoice_file_id)} data-testid={`payment-invoice-${p.id}`}><FileText className="mr-1 h-3 w-3" />Счёт</Button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ UPLOAD PANEL ============
function UploadPanel({ clientId, onUploaded }) {
  const { api } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [assetType, setAssetType] = useState('other');

  const typeOptions = [
    { value: 'contract', label: 'Договор' },
    { value: 'signed_contract', label: 'Подписанный договор' },
    { value: 'invoice', label: 'Счёт' },
    { value: 'payment_proof', label: 'Подтверждение оплаты' },
    { value: 'receipt', label: 'Квитанция' },
    { value: 'estimate', label: 'Смета' },
    { value: 'other', label: 'Другое' },
  ];

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('asset_type', assetType);
    fd.append('title', file.name);
    fd.append('visible_to_client', assetType === 'invoice' || assetType === 'receipt' || assetType === 'signed_contract');
    try {
      await api.post(`/clients/${clientId}/assets/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Файл загружен');
      onUploaded?.();
    } catch { toast.error('Ошибка загрузки'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  return (
    <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
      <Select value={assetType} onValueChange={setAssetType}>
        <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="upload-asset-type-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {typeOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <label className="cursor-pointer flex-1">
        <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
        <Button size="sm" variant="default" asChild disabled={uploading} className="w-full" data-testid="upload-asset-btn">
          <span>{uploading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Upload className="mr-1 h-3 w-3" />}Загрузить файл</span>
        </Button>
      </label>
    </div>
  );
}

// ============ MAIN SHEET ============
export function ClientDetailSheet({ client, open, onOpenChange, onEdit, onRefresh, onCreateOrder }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshKey, setRefreshKey] = useState(0);

  // Reset to overview when opening a different client
  useEffect(() => {
    if (open) { setActiveTab('overview'); setRefreshKey(k => k + 1); }
  }, [open, client?.id]);

  if (!client) return null;

  const handleDelete = async () => {};

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl overflow-y-auto" data-testid="client-detail-sheet">
        <SheetHeader className="pb-3">
          <SheetTitle className="flex items-center gap-2"><Users className="h-5 w-5" />{client.name}</SheetTitle>
          <SheetDescription>Карточка клиента</SheetDescription>
        </SheetHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
          <TabsList className="grid w-full grid-cols-6 h-9" data-testid="client-detail-tabs">
            <TabsTrigger value="overview" className="text-xs px-1" data-testid="tab-overview">Обзор</TabsTrigger>
            <TabsTrigger value="orders" className="text-xs px-1" data-testid="tab-orders">Заказы</TabsTrigger>
            <TabsTrigger value="activity" className="text-xs px-1" data-testid="tab-activity"><Activity className="h-3 w-3 mr-1 hidden sm:inline" />Лог</TabsTrigger>
            <TabsTrigger value="photos" className="text-xs px-1" data-testid="tab-photos">Фото</TabsTrigger>
            <TabsTrigger value="documents" className="text-xs px-1" data-testid="tab-documents">Док-ты</TabsTrigger>
            <TabsTrigger value="payments" className="text-xs px-1" data-testid="tab-payments">Платежи</TabsTrigger>
          </TabsList>

          <div className="mt-4">
            <TabsContent value="overview" className="mt-0">
              <OverviewTab client={client} onEdit={onEdit} onCreateOrder={onCreateOrder} onOpenChange={onOpenChange} />
            </TabsContent>
            <TabsContent value="orders" className="mt-0">
              <OrdersTab clientId={client.id} key={`orders-${refreshKey}`} />
            </TabsContent>
            <TabsContent value="activity" className="mt-0">
              <ClientActivityTab clientId={client.id} key={`activity-${refreshKey}`} />
            </TabsContent>
            <TabsContent value="photos" className="mt-0">
              <PhotosTab clientId={client.id} key={`photos-${refreshKey}`} />
            </TabsContent>
            <TabsContent value="documents" className="mt-0">
              <DocumentsTab clientId={client.id} key={`docs-${refreshKey}`} />
              <div className="mt-4">
                <UploadPanel clientId={client.id} onUploaded={() => setRefreshKey(k => k + 1)} />
              </div>
            </TabsContent>
            <TabsContent value="payments" className="mt-0">
              <PaymentsTab clientId={client.id} key={`pay-${refreshKey}`} />
            </TabsContent>
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
